import { activePassengerForAddons } from "../../../Actions/LogicActions/Addons/ActivePassengerForAddons";
import { ConfirmAddonsAction } from "../../../Actions/LogicActions/Addons/ConfirmAddonsAction";
import { getAddonsFromBookingAction } from "../../../Actions/LogicActions/Addons/getAddonsFromBookingAction";
import { onChangeAddonForActivePassenger } from "../../../Actions/LogicActions/Addons/OnChangeAddonForActivePassenger";
import { preparePassengerForAddons } from "../../../Actions/LogicActions/Addons/PreparePassengerForAddons";
import { resetLegAddonsData } from "../../../Actions/LogicActions/Addons/ResetLegAddonsData";
import { persistAddonsOnConfirmAction } from "../../../Actions/LogicActions/Addons/PersistAddonsOnConfirmAction";
import { setCarLicensePlateTempAction } from "../../../Actions/LogicActions/Addons/SetCarLicensePlateAction";
import { setExistingAddonsAction } from "../../../Actions/LogicActions/Addons/SetExistingAddons";
import { syncAlreadyAddonsAction } from "../../../Actions/LogicActions/Addons/SyncAlreadyAddonsAction";
import { openAddonsModalAction } from "../../../Actions/UIActions/Addons/OpenAddonsModalAction";
import { _eventBus } from "../../../core/events/EventBus";
import { StepDefinition } from "../../../core/flows/StepDefinition";
import { StepFlow } from "../../../core/flows/StepFlow";
import { isLiveChangeDateCheckout } from "../../../Lib/Utils/changeDate";
import { isActionRunSuccessfully } from "../../../Lib/Utils/validationUtil";
import { evtTree } from "../../../Orchestrator/ConstantTree";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import {
  IActivePassengerForAddons,
  IGetAddonsParams,
  IOnChangeAddonForActivePassenger,
  IPreparePassengerForAddons,
} from "../../../Orchestrator/Types/Interface/ActionParams/GetAddonsParams";
import {
  IConfirmAddonsActionParams,
  ISetExistingAddonsAction,
  ITransformedMealForLegs
} from "../../../Orchestrator/Types/Interface/IAddOns";
import { AddOnsContext } from "../Context/AddOnsContext";

class AddOnsInitStep extends StepFlow {
  get stepName() {
    return "AddOnsInit_Step";
  }

  async onEventSetup(): Promise<void> {
    // FlowRunner re-instantiates the step on every flow re-entry, so prior-
    // instance handlers may still be attached to `_eventBus`. Drain them via
    // the base-class static registry before registering fresh ones — this
    // covers both cross-instance and same-instance resume cases. Without it,
    // a single Confirm click in change-date checkout fan-outs to every
    // accumulated handler and POSTs the same ancillary multiple times.
    await this.drainPersistentSubscriptions();

    this.registerPersistentEventSubscription(
      _eventBus.on<IGetAddonsParams, void>({
        eventName: evtTree.AddOns.Api.GetBookingOffersAddOns,
        handler: async ({ data }) => {
          const { bookingId = "", bookedOffers = [], bookedTrips = [], isUpgrade = false } = data!;
          let addonsContext = this.getCtx<AddOnsContext>("addonsContext")!;
          if (addonsContext) {
            addonsContext.bookingId = bookingId;
            addonsContext.bookedOffers = bookedOffers;
            addonsContext.bookedTrips = bookedTrips;
            addonsContext.isUpgrade = isUpgrade;

            // Seed `alreadyAddonsLegPassengerAtom` from the current booking
            // so PreparePassengerForAddons can pre-check existing addons in
            // the modal. Two flows need this:
            //   - manage-seating: pre-select FULFILLED ancillaries the user
            //     already paid for.
            //   - change-date: pre-select carry-over (REFUNDED on EXCHANGED
            //     admissions) ancillaries so the user keeps what they had on
            //     the original offer. SyncAlreadyAddonsAction internally
            //     gates the REFUNDED filter on the same isChangeDateFlow
            //     localStorage marker.
            // Only updates addon-specific atoms — bookingResponseAtom is not
            // touched. Upgrade / regular checkout still skip this seed.
            const isManageSeating =
              (getRecoilStateValue("isManageSeatingAtom") as boolean) || false;
            const isChangeDateFlow =
              isLiveChangeDateCheckout() && !isManageSeating && !isUpgrade;
            if (isManageSeating || isChangeDateFlow) {
              syncAlreadyAddonsAction.setParams({ isUpgrade });
              await syncAlreadyAddonsAction.execute();
            }

            getAddonsFromBookingAction.setParams({
              ...addonsContext,
            });
            await getAddonsFromBookingAction.execute();

            if (isActionRunSuccessfully(getAddonsFromBookingAction)) {
              addonsContext.listOfLagsAddons =
                (getAddonsFromBookingAction?.result ||
                  {}) as ITransformedMealForLegs;
            }
          }
        },
      })
    );

    this.registerPersistentEventSubscription(
      _eventBus.on<{ value: string }, void>({
        eventName: evtTree.AddOns.Data.CarLicensePlate.Change,
        handler: async ({ data }) => {
          setCarLicensePlateTempAction.setParams({ value: data?.value || "" });
          await setCarLicensePlateTempAction.execute();
        },
      })
    );

    this.registerPersistentEventSubscription(
      _eventBus.on<boolean, void>({
        eventName: evtTree.AddOns.OpenPopup.Click,
        handler: async ({ data }) => {
          await openAddonsModalAction.setParams({ isModalOpen: data! });
          await openAddonsModalAction.execute();
        },
      })
    );
    this.registerPersistentEventSubscription(
      _eventBus.on<IPreparePassengerForAddons, void>({
        eventName: evtTree.AddOns.Passenger.Prepare,
        handler: async ({ data }) => {
          if (data) {
            preparePassengerForAddons.setParams({ ...data });
            await preparePassengerForAddons.execute();
          }
        },
      })
    );
    this.registerPersistentEventSubscription(
      _eventBus.on<IActivePassengerForAddons, void>({
        eventName: evtTree.AddOns.Passenger.Select,
        handler: async ({ data }) => {
          if (data) {
            activePassengerForAddons.setParams(data);
            await activePassengerForAddons.execute();
          }
        },
      })
    );
    this.registerPersistentEventSubscription(
      _eventBus.on<IOnChangeAddonForActivePassenger, void>({
        eventName: evtTree.AddOns.OpenPopup.onChange,
        handler: async ({ data }) => {
          if (data) {
            onChangeAddonForActivePassenger.setParams(data);
            await onChangeAddonForActivePassenger.execute();
          }
        },
      })
    );
    this.registerPersistentEventSubscription(
      _eventBus.on<IActivePassengerForAddons, void>({
        eventName: evtTree.AddOns.Data.reset,
        handler: async ({ data }) => {
          if (data) {
            resetLegAddonsData.setParams(data);
            await resetLegAddonsData.execute();
          }
        },
      })
    );
    this.registerPersistentEventSubscription(
      _eventBus.on<IConfirmAddonsActionParams, void>({
        eventName: evtTree.AddOns.Data.Confirm,
        handler: async ({ data }) => {
          if (data) {
            const confirmAddonsAction = new ConfirmAddonsAction();
            confirmAddonsAction.setParams(data);
            await confirmAddonsAction.execute();

            const patchOnConfirm =
              (getRecoilStateValue("patchAddonsOnConfirmAtom") as boolean) ?? true;
            if (patchOnConfirm) {
              // Scope the persist to legs in this confirm round. AddonsModal
              // emits one leg at a time; passing its legIds prevents the
              // accumulated confirmAddonsPassengerListAtom (which also holds
              // prior-round legs) from causing cross-leg DELETEs.
              const scopeLegIds = (data.addonsPassengerList || [])
                .map((l) => l?.legId)
                .filter((id): id is string => !!id);
              persistAddonsOnConfirmAction.setParams({ scopeLegIds });
              await persistAddonsOnConfirmAction.execute();
            }
          }
        },
      })
    );
    this.registerPersistentEventSubscription(
      _eventBus.on<ISetExistingAddonsAction, void>({
        eventName: evtTree.AddOns.Data.setExisting,
        handler: async ({ data }) => {
          if (data) {
            setExistingAddonsAction.setParams(data);
            await setExistingAddonsAction.execute();
          }
        },
      })
    );
    this.registerPersistentEventSubscription(
      _eventBus.on<ISetExistingAddonsAction, void>({
        eventName: evtTree.AddOns.Flow.Start,
        handler: async ({ data }) => {
          updateRecoilState("addonFlowStartedAtom", true)
        },
      })
    );
  }

  async onExecute(): Promise<void> {
    console.log("AddOnsInit_Step Executed...............");
    _eventBus.emit({
      eventName: evtTree.AddOns.Flow.Start,
    });
  }
}

export const addOnsInitStepDeclaration = new StepDefinition({
  stepName: "AddOnsInit_Step",
  stepClass: AddOnsInitStep,
});
